import React, { useEffect } from 'react';
import { ArrowDownToLine, FileSpreadsheet, FileText, X } from 'lucide-react';
import { formatInr } from '../../utils/reconciliation/formatters';

const SHEETS = [
  'Summary',
  'Client Transactions',
  'Owner Transactions',
  'Treasury & Internal',
  'All Transactions',
  'GST Analysis',
  'Payment Channels',
];

export default function ExportPreviewModal({
  open,
  onClose,
  onConfirm,
  onExportCsv,
  exporting,
  csvExporting = false,
  summary,
  previewRows = [],
  filename,
}) {
  const busy = exporting || csvExporting;

  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('recon-export-modal-open');
    return () => document.body.classList.remove('recon-export-modal-open');
  }, [open]);

  if (!open) return null;

  return (
    <div className="recon-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="recon-preview-title">
      <div className="recon-modal-panel">
        <div className="recon-modal-header flex items-start justify-between gap-3 border-b border-de-gold/20 px-5 py-4">
          <div>
            <p className="m-0 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-de-gold/80">
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Export preview
            </p>
            <h3 id="recon-preview-title" className="m-0 mt-1 text-lg font-black text-white">
              Financial Report
            </h3>
            <p className="m-0 mt-1 text-xs text-de-muted">
              Excel workbook or CSV · opens on mobile
            </p>
          </div>
          <button type="button" className="recon-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="recon-modal-body space-y-4 px-5 py-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { label: 'Rows', value: summary?.count ?? 0 },
              { label: 'Credits', value: formatInr(summary?.totalCredits) },
              { label: 'Debits', value: formatInr(summary?.totalDebits) },
              { label: 'Net', value: formatInr(summary?.netBalance) },
            ].map((item) => (
              <div key={item.label} className="recon-glass-chip">
                <p className="m-0 text-[10px] uppercase tracking-wider text-de-muted">{item.label}</p>
                <p className="m-0 mt-1 text-sm font-bold text-white">{item.value}</p>
              </div>
            ))}
          </div>

          <div>
            <p className="m-0 mb-2 text-[10px] font-bold uppercase tracking-wider text-de-gold/75">
              Workbook sheets
            </p>
            <div className="flex flex-wrap gap-2">
              {SHEETS.map((name) => (
                <span key={name} className="recon-filter-chip">{name}</span>
              ))}
            </div>
          </div>

          <div>
            <p className="m-0 mb-2 text-[10px] font-bold uppercase tracking-wider text-de-gold/75">
              Sample transactions (first {Math.min(previewRows.length, 8)})
            </p>
            <div className="recon-preview-table-wrap">
              <table className="recon-preview-table">
                <thead>
                  <tr>
                    {['Date', 'Category', 'Client', 'Type', 'Channel', 'Credit', 'Debit', 'Status'].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.slice(0, 8).map((row) => (
                    <tr key={row.id || row.receiptRef}>
                      <td>{row.displayDate}</td>
                      <td>{row.categoryLabel || 'Internal'}</td>
                      <td title={row.clientName}>{row.clientName}</td>
                      <td>{row.typeLabel}</td>
                      <td>{row.channelLabel}</td>
                      <td className="num">{row.credit ? row.credit.toLocaleString('en-IN') : '—'}</td>
                      <td className="num">{row.debit ? row.debit.toLocaleString('en-IN') : '—'}</td>
                      <td>{row.statusLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="m-0 rounded-lg border border-de-gold/20 bg-black/30 px-3 py-2 text-xs text-de-muted">
            Use <strong className="text-de-gold">Download Excel (.xlsx)</strong> for the premium 7-sheet report
            (Client · Owner · Treasury · All · Summary · GST · Channels).
            CSV is plain data only (no colours).
            In Excel mobile, turn off <strong className="text-de-gold">Mobile View</strong> to see navy headers and formatting.
            <br />
            File: <span className="font-mono text-de-gold">{filename}</span>
          </p>
        </div>

        <div className="recon-modal-footer flex flex-col gap-2 border-t border-de-gold/20 px-5 py-4 sm:flex-row sm:flex-wrap sm:justify-end">
          <button type="button" className="recon-btn-ghost w-full sm:w-auto" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {onExportCsv ? (
            <button type="button" className="recon-btn-primary w-full sm:w-auto" onClick={onExportCsv} disabled={busy}>
              {csvExporting ? (
                <>
                  <span className="recon-spinner" />
                  Building CSV…
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4" />
                  Download CSV
                </>
              )}
            </button>
          ) : null}
          <button type="button" className="recon-btn-primary w-full sm:w-auto" onClick={onConfirm} disabled={busy}>
            {exporting ? (
              <>
                <span className="recon-spinner" />
                Building workbook…
              </>
            ) : (
              <>
                <ArrowDownToLine className="h-4 w-4" />
                Download Excel (.xlsx)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
